/**
 * Dashboard Widgets API Client
 * Handles CRUD operations for user-customizable dashboard widgets
 */

import apiClient from './client';

// Types
export interface FilterConfig {
  assignee?: 'me' | 'anyone' | 'unassigned' | string;
  categories?: string[];
  tags?: string[];
  tagsMatch?: 'any' | 'all';
  status?: ('pending' | 'in_progress' | 'completed' | 'overdue')[];
  dueDateRange?: 'today' | 'next_3_days' | 'next_7_days' | 'next_14_days' | 'next_30_days' | 'this_month' | 'this_quarter' | 'custom';
  dueDateFrom?: string;
  dueDateTo?: string;
  templateId?: string;
}

export interface DisplayConfig {
  maxItems?: number;
  sortBy?: 'due_date' | 'created_at' | 'title' | 'category' | 'assignee';
  sortOrder?: 'asc' | 'desc';
  showAssignee?: boolean;
  showCategory?: boolean;
  showDueDate?: boolean;
  groupBy?: 'category' | 'assignee' | 'status' | 'none';
}

export interface DashboardWidget {
  id: string;
  tenant_id: string;
  user_id: string;
  title: string;
  widget_type: 'list' | 'count' | 'calendar';
  filter_config: FilterConfig;
  display_config: DisplayConfig;
  grid_x: number;
  grid_y: number;
  grid_width: number;
  grid_height: number;
  sort_order: number;
  is_visible: boolean;
  created_at: string;
  updated_at: string;
}

export interface WidgetPreset {
  id: string;
  tenant_id: string | null;
  name: string;
  description: string | null;
  icon: string | null;
  widget_type: 'list' | 'count' | 'calendar';
  filter_config: FilterConfig;
  display_config: DisplayConfig;
  default_width: number;
  default_height: number;
  is_system: boolean;
  is_shared: boolean;
  created_by_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface WidgetDataItem {
  id: string;
  due_date: string;
  status: string;
  completed_at: string | null;
  created_at: string;
  template_id: string;
  template_title: string;
  category_id: string | null;
  category_name: string | null;
  category_color: string | null;
  assignee_id: string | null;
  assignee_name: string | null;
  is_overdue: number;
}

export interface WidgetData {
  count?: number;
  items?: WidgetDataItem[];
  dates?: Record<string, WidgetDataItem[]>;
}

export interface CreateWidgetInput {
  title: string;
  widget_type: 'list' | 'count' | 'calendar';
  filter_config?: FilterConfig;
  display_config?: DisplayConfig;
  grid_x?: number;
  grid_y?: number;
  grid_width?: number;
  grid_height?: number;
}

export interface UpdateWidgetInput {
  title?: string;
  widget_type?: 'list' | 'count' | 'calendar';
  filter_config?: FilterConfig;
  display_config?: DisplayConfig;
  grid_x?: number;
  grid_y?: number;
  grid_width?: number;
  grid_height?: number;
  sort_order?: number;
  is_visible?: boolean;
}

export interface BulkUpdateWidget {
  id: string;
  grid_x?: number;
  grid_y?: number;
  grid_width?: number;
  grid_height?: number;
  sort_order?: number;
}

// API Functions
export const dashboardWidgetsApi = {
  /**
   * Get all widgets for the current user
   */
  list: async (): Promise<DashboardWidget[]> => {
    const response = await apiClient.get('/v1/dashboard-widgets');
    return response.data.widgets;
  },

  /**
   * Get available widget presets
   */
  listPresets: async (): Promise<WidgetPreset[]> => {
    const response = await apiClient.get('/v1/dashboard-widgets/presets');
    return response.data.presets;
  },

  /**
   * Create a new widget
   */
  create: async (data: CreateWidgetInput): Promise<DashboardWidget> => {
    const response = await apiClient.post('/v1/dashboard-widgets', data);
    return response.data.widget;
  },

  /**
   * Create a widget from a preset
   */
  createFromPreset: async (presetId: string): Promise<DashboardWidget> => {
    const response = await apiClient.post(`/v1/dashboard-widgets/from-preset/${presetId}`);
    return response.data.widget;
  },

  /**
   * Update a widget
   */
  update: async (id: string, data: UpdateWidgetInput): Promise<DashboardWidget> => {
    const response = await apiClient.patch(`/v1/dashboard-widgets/${id}`, data);
    return response.data.widget;
  },

  /**
   * Bulk update widgets (for drag-drop reordering)
   */
  bulkUpdate: async (widgets: BulkUpdateWidget[]): Promise<void> => {
    await apiClient.patch('/v1/dashboard-widgets/bulk-update', { widgets });
  },

  /**
   * Delete a widget
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/v1/dashboard-widgets/${id}`);
  },

  /**
   * Get widget data (execute query)
   */
  getData: async (id: string): Promise<WidgetData> => {
    const response = await apiClient.get(`/v1/dashboard-widgets/${id}/data`);
    return response.data.data;
  },
};

export default dashboardWidgetsApi;
